import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import { toastError, toastSuccess } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { inputCls, textareaCls, labelCls, StyledSelect, btnSave, btnSecondary } from "../../components/admin/FormComponents"
import { StudentAssignmentPicker, type StudentOption } from "../../components/admin/StudentAssignmentPicker"
import { GroupCredentialPanel } from "../../components/admin/GroupCredentialPanel"
import { SearchMultiSelect } from "../../components/admin/SearchMultiSelect"
import { EntityDetailNavLayout, type EntityDetailNavGroup } from "../../components/admin/EntityDetailNav"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { Toggle } from "../../components/ui/Toggle"
import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import { TrashBinIcon } from "../../icons"
import { LoadState } from "@/components/ui/LoadState"

const ROSTER_PAGE_SIZE = 10

type GroupType = "individual" | "shared" | "team" | "edition"
type GroupManagerTabKey = "identity" | "students" | "seedbeds"

type SeedbedSummary = { id: string; name: string }
type MemberSummary = StudentOption

type GroupDetail = {
  id: string
  name: string
  description: string | null
  groupType: GroupType
  cccdTeamId: string | null
  traCertificationEditionId: string | null
  year: number | null
  allowGroupLogin: boolean
  credential: { loginCode: string; isActive: boolean } | null
  seedbeds: SeedbedSummary[]
  members: MemberSummary[]
}

const GROUP_TYPE_LABELS: Record<"individual" | "shared", string> = {
  individual: "Individual",
  shared: "Compartido",
}

const LOCKED_GROUP_SOURCE: Record<"team" | "edition", { label: string; toPrefix: string }> = {
  team: { label: "un equipo CCCD", toPrefix: "/cccd/teams" },
  edition: { label: "una edición de certificación Tripoli", toPrefix: "/tra/editions" },
}

const TAB_LABELS: Record<GroupManagerTabKey, string> = {
  identity: "Identidad",
  students: "Estudiantes",
  seedbeds: "Semilleros",
}

export default function GroupManagerPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<GroupManagerTabKey>("identity")
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [groupType, setGroupType] = useState<"individual" | "shared">("individual")
  const [lockedGroupType, setLockedGroupType] = useState<"team" | "edition" | null>(null)
  const [lockedSourceId, setLockedSourceId] = useState<string | null>(null)
  const isLockedGroup = lockedGroupType !== null
  const [allowGroupLogin, setAllowGroupLogin] = useState(false)
  const [credential, setCredential] = useState<{ loginCode: string; isActive: boolean } | null>(null)
  const [togglingGroupLogin, setTogglingGroupLogin] = useState(false)

  const [assignedSeedbeds, setAssignedSeedbeds] = useState<SeedbedSummary[]>([])
  const [pendingSeedbeds, setPendingSeedbeds] = useState<SeedbedSummary[]>([]) // selected in the picker, not yet assigned
  const [assigningSeedbeds, setAssigningSeedbeds] = useState(false)
  const [unassigningSeedbedId, setUnassigningSeedbedId] = useState<string | null>(null)

  const [members, setMembers] = useState<MemberSummary[]>([])
  const [pendingStudents, setPendingStudents] = useState<MemberSummary[]>([]) // selected in the picker, not yet added
  const [addingStudents, setAddingStudents] = useState(false)
  const [removingStudentId, setRemovingStudentId] = useState<string | null>(null)
  const [seedbedToUnassign, setSeedbedToUnassign] = useState<SeedbedSummary | null>(null)
  const [membersPage, setMembersPage] = useState(1)
  const [seedbedsPage, setSeedbedsPage] = useState(1)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    api
      .get(`/academy/seedbeds/admin/groups/${id}`)
      .then((res) => {
        const d = res.data?.data as GroupDetail
        setName(d.name)
        setDescription(d.description ?? "")
        setGroupType(d.groupType === "shared" ? "shared" : "individual")
        setLockedGroupType(d.groupType === "team" ? "team" : d.groupType === "edition" ? "edition" : null)
        setLockedSourceId(d.groupType === "team" ? d.cccdTeamId : d.groupType === "edition" ? d.traCertificationEditionId : null)
        setAllowGroupLogin(d.allowGroupLogin)
        setCredential(d.credential)
        setAssignedSeedbeds(d.seedbeds)
        setMembers(d.members)
      })
      .catch((err) => setError(getErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [id])

  function goBack() {
    navigate("/instructor/groups")
  }

  async function onSubmitIdentity(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError("")
    try {
      if (isEdit && id) {
        await api.put(`/academy/seedbeds/admin/groups/${id}`, { name, description: description || undefined, group_type: groupType })
        toastSuccess("Grupo actualizado")
      } else {
        // El grupo se crea sin semillero asociado — asociarlo (a uno o
        // varios, opcional) y armar el roster son pasos aparte, después de
        // guardar, desde las pestañas "Semilleros" y "Estudiantes".
        const res = await api.post("/academy/seedbeds/admin/groups", {
          name,
          description: description || undefined,
          group_type: groupType,
        })
        toastSuccess("Grupo creado")
        const newId = res.data?.data?.id
        navigate(newId ? `/instructor/groups/${newId}` : "/instructor/groups")
        return
      }
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  async function onToggleGroupLogin(enabled: boolean) {
    if (!id) return
    setTogglingGroupLogin(true)
    try {
      await api.put(`/academy/seedbeds/admin/groups/${id}`, { allow_group_login: enabled })
      setAllowGroupLogin(enabled)
      if (!enabled) setCredential((prev) => (prev ? { ...prev, isActive: false } : prev))
      toastSuccess(enabled ? "Login de equipo habilitado" : "Login de equipo deshabilitado")
    } catch (err) {
      toastError(err)
    } finally {
      setTogglingGroupLogin(false)
    }
  }

  async function onAssignSeedbeds() {
    if (!id || pendingSeedbeds.length === 0) return
    setAssigningSeedbeds(true)
    try {
      await Promise.all(
        pendingSeedbeds.map((s) => api.post(`/academy/seedbeds/admin/groups/${id}/seedbeds`, { seedbed_id: s.id }))
      )
      setAssignedSeedbeds((prev) => [...prev, ...pendingSeedbeds])
      toastSuccess(`Grupo asignado a ${pendingSeedbeds.length} semillero${pendingSeedbeds.length > 1 ? "s" : ""}`)
      setPendingSeedbeds([])
    } catch (err) {
      toastError(err)
    } finally {
      setAssigningSeedbeds(false)
    }
  }

  async function onUnassignSeedbed(seedbedId: string) {
    if (!id) return
    setUnassigningSeedbedId(seedbedId)
    try {
      await api.delete(`/academy/seedbeds/admin/groups/${id}/seedbeds/${seedbedId}`)
      setAssignedSeedbeds((prev) => prev.filter((s) => s.id !== seedbedId))
      toastSuccess("Grupo removido del semillero")
    } catch (err) {
      toastError(err)
    } finally {
      setUnassigningSeedbedId(null)
    }
  }

  async function onAddStudentsToRoster() {
    if (!id || pendingStudents.length === 0) return
    setAddingStudents(true)
    try {
      await api.post(`/academy/seedbeds/admin/groups/${id}/students`, {
        user_ids: pendingStudents.map((s) => s.id),
      })
      setMembers((prev) => [...prev, ...pendingStudents])
      toastSuccess(`${pendingStudents.length} estudiante${pendingStudents.length > 1 ? "s" : ""} agregado${pendingStudents.length > 1 ? "s" : ""} al grupo`)
      setPendingStudents([])
    } catch (err) {
      toastError(err)
    } finally {
      setAddingStudents(false)
    }
  }

  async function onRemoveStudent(userId: string) {
    if (!id) return
    setRemovingStudentId(userId)
    try {
      await api.delete(`/academy/seedbeds/admin/groups/${id}/students/${userId}`)
      setMembers((prev) => prev.filter((m) => m.id !== userId))
      toastSuccess("Estudiante quitado del grupo")
    } catch (err) {
      toastError(err)
    } finally {
      setRemovingStudentId(null)
    }
  }

  function toggleStudent(student: MemberSummary) {
    setPendingStudents((prev) => (prev.some((s) => s.id === student.id) ? prev.filter((s) => s.id !== student.id) : [...prev, student]))
  }

  function toggleSeedbed(seedbed: SeedbedSummary) {
    setPendingSeedbeds((prev) => (prev.some((s) => s.id === seedbed.id) ? prev.filter((s) => s.id !== seedbed.id) : [...prev, seedbed]))
  }

  const memberColumns: DataTableColumn<MemberSummary>[] = [
    {
      key: "name",
      label: "Estudiante",
      render: (m) => (
        <>
          <p className="font-medium text-gray-900 dark:text-white">{m.name}</p>
          <p className="text-xs text-gray-500">{m.email}</p>
        </>
      ),
    },
    {
      key: "institution",
      label: "Institución / Documento",
      render: (m) => (
        <>
          {m.institution && <p className="text-sm text-gray-700 dark:text-gray-300">{m.institution}</p>}
          {m.documentNumber && <p className="text-xs text-gray-500">Doc. {m.documentNumber}</p>}
          {!m.institution && !m.documentNumber && <span className="text-xs text-gray-500 dark:text-gray-400 italic">Sin datos</span>}
        </>
      ),
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (m) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              {
                label: removingStudentId === m.id ? "Quitando..." : "Quitar del grupo",
                icon: <TrashBinIcon />,
                onClick: () => void onRemoveStudent(m.id),
                variant: "danger",
                disabled: removingStudentId === m.id,
              },
            ]}
          />
        </div>
      ),
    },
  ]

  const seedbedColumns: DataTableColumn<SeedbedSummary>[] = [
    { key: "name", label: "Semillero" },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (s) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              {
                label: unassigningSeedbedId === s.id ? "Quitando..." : "Quitar de este semillero",
                icon: <TrashBinIcon />,
                onClick: () => setSeedbedToUnassign(s),
                variant: "danger",
                disabled: unassigningSeedbedId === s.id,
              },
            ]}
          />
        </div>
      ),
    },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadState />
      </div>
    )
  }

  // Al crear solo "Identidad" está disponible: asociar el grupo a un
  // semillero y armar su roster son opcionales y requieren que el grupo ya
  // exista, así que "Semilleros" y "Estudiantes" quedan bloqueadas hasta
  // guardar — igual que el flujo de creación de un estudiante.
  const locked = (key: GroupManagerTabKey) => !isEdit && key !== "identity"
  const groups: EntityDetailNavGroup<GroupManagerTabKey>[] = [
    { label: "GENERAL", items: [
      { key: "identity", label: TAB_LABELS.identity, locked: locked("identity") },
    ] },
    { label: "ASIGNACIONES", items: [
      { key: "students", label: TAB_LABELS.students, locked: locked("students") },
      { key: "seedbeds", label: TAB_LABELS.seedbeds, locked: locked("seedbeds") },
    ] },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Docentes",
        { label: "Grupos", to: "/instructor/groups" },
        isEdit ? name || "Grupo" : "Nuevo",
      ]}
      title={isEdit ? name || "Grupo" : "Nuevo grupo"}
      subtitle="Agrupa estudiantes una sola vez y asígnalos, si quieres, a uno o varios semilleros"
      tabs={[{ label: "Grupos", to: "/instructor/groups", active: true }]}
      metrics={[]}
    >
      <EntityDetailNavLayout groups={groups} activeKey={activeTab} onChange={(key) => !locked(key) && setActiveTab(key)}>
      <form onSubmit={onSubmitIdentity}>
        {activeTab === "identity" && (
          <div className="space-y-5">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
              <h3 className="mb-3 text-sm font-semibold text-gray-800 dark:text-white/90">Identidad</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Nombre *</label>
                  <input
                    required
                    autoFocus
                    disabled={isLockedGroup}
                    placeholder="Ej: Colegio San José – Gr. A"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Tipo</label>
                  <StyledSelect
                    value={groupType}
                    disabled={isLockedGroup}
                    onChange={(e) => setGroupType(e.target.value as "individual" | "shared")}
                  >
                    {Object.entries(GROUP_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </StyledSelect>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Descripción (opcional)</label>
                  <textarea
                    rows={2}
                    disabled={isLockedGroup}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className={textareaCls}
                  />
                </div>
              </div>
            </div>

            {isEdit && id && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03] space-y-3">
                <Toggle
                  checked={allowGroupLogin}
                  disabled={togglingGroupLogin}
                  onChange={(v) => void onToggleGroupLogin(v)}
                  label="Login de equipo"
                  description="Permite que todo el equipo entre con un solo usuario y contraseña, independiente del tipo de grupo — funciona incluso para equipos CCCD sincronizados. Cada estudiante sigue acumulando su propio avance por separado."
                />

                {allowGroupLogin && (
                  <GroupCredentialPanel
                    groupId={id}
                    existingLoginCode={credential?.isActive ? credential.loginCode : null}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "students" && isEdit && isLockedGroup && lockedGroupType && (
          <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 px-4 py-3 text-sm text-purple-700 dark:text-purple-300">
            Este grupo está sincronizado con {LOCKED_GROUP_SOURCE[lockedGroupType].label} — sus estudiantes se gestionan
            desde{" "}
            {lockedSourceId ? (
              <Link to={`${LOCKED_GROUP_SOURCE[lockedGroupType].toPrefix}/${lockedSourceId}`} className="underline">
                su ficha de origen
              </Link>
            ) : (
              "su ficha de origen"
            )}
            , no aquí.
          </div>
        )}

        {activeTab === "students" && isEdit && !isLockedGroup && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
              <h3 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">
                Estudiantes {members.length > 0 ? `(${members.length})` : ""}
              </h3>
              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                Busca y selecciona estudiantes para agregarlos al grupo — quedarán inscritos en todos los semilleros a los que ya esté asignado.
              </p>

              <StudentAssignmentPicker
                selectedStudents={pendingStudents}
                excludeIds={members.map((m) => m.id)}
                onToggleStudent={toggleStudent}
              />

              <button
                type="button"
                disabled={pendingStudents.length === 0 || addingStudents}
                onClick={() => void onAddStudentsToRoster()}
                className="mt-3 min-h-11 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
              >
                {addingStudents ? "Agregando..." : `Agregar ${pendingStudents.length > 0 ? `${pendingStudents.length} estudiante${pendingStudents.length > 1 ? "s" : ""}` : "estudiantes"} al grupo`}
              </button>
            </div>

            <DataTable
              columns={memberColumns}
              rows={members.slice((membersPage - 1) * ROSTER_PAGE_SIZE, membersPage * ROSTER_PAGE_SIZE)}
              rowKey={(m) => m.id}
              emptyMessage="Sin estudiantes en este grupo aún."
              page={membersPage}
              pageSize={ROSTER_PAGE_SIZE}
              total={members.length}
              onPageChange={setMembersPage}
            />
          </div>
        )}

        {activeTab === "seedbeds" && isEdit && isLockedGroup && lockedGroupType && (
          <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 px-4 py-3 text-sm text-purple-700 dark:text-purple-300">
            Este grupo está sincronizado con {LOCKED_GROUP_SOURCE[lockedGroupType].label} — sus semilleros se gestionan
            desde{" "}
            {lockedSourceId ? (
              <Link to={`${LOCKED_GROUP_SOURCE[lockedGroupType].toPrefix}/${lockedSourceId}`} className="underline">
                su ficha de origen
              </Link>
            ) : (
              "su ficha de origen"
            )}
            , no aquí.
          </div>
        )}

        {activeTab === "seedbeds" && isEdit && !isLockedGroup && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
              <h3 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">
                Semilleros asignados {assignedSeedbeds.length > 0 ? `(${assignedSeedbeds.length})` : ""}
              </h3>
              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                Opcional — un grupo puede no estar asignado a ningún semillero, o a varios a la vez.
              </p>

              <SearchMultiSelect
                selectedOptions={pendingSeedbeds.map((s) => ({ id: s.id, label: s.name }))}
                onToggle={(option) => {
                  const existing = pendingSeedbeds.find((s) => s.id === option.id)
                  toggleSeedbed(existing ?? { id: option.id, name: option.label })
                }}
                onSearch={async (query) => {
                  const res = await api.get("/academy/seedbeds/admin", { params: { search: query, limit: 20 } })
                  const results: SeedbedSummary[] = res.data?.data?.seedbeds ?? []
                  const assignedIds = new Set(assignedSeedbeds.map((s) => s.id))
                  return results.filter((s) => !assignedIds.has(s.id)).map((s) => ({ id: s.id, label: s.name }))
                }}
                placeholder="Buscar semillero por nombre..."
                emptyMessage="No hay semilleros con ese criterio."
              />

              {pendingSeedbeds.length > 0 && (
                <button
                  type="button"
                  disabled={assigningSeedbeds}
                  onClick={() => void onAssignSeedbeds()}
                  className={`mt-3 min-h-11 ${btnSecondary}`}
                >
                  {assigningSeedbeds ? "Asignando..." : `Asignar ${pendingSeedbeds.length} semillero${pendingSeedbeds.length > 1 ? "s" : ""}`}
                </button>
              )}
            </div>

            <DataTable
              columns={seedbedColumns}
              rows={assignedSeedbeds.slice((seedbedsPage - 1) * ROSTER_PAGE_SIZE, seedbedsPage * ROSTER_PAGE_SIZE)}
              rowKey={(s) => s.id}
              emptyMessage="Sin semilleros asignados."
              page={seedbedsPage}
              pageSize={ROSTER_PAGE_SIZE}
              total={assignedSeedbeds.length}
              onPageChange={setSeedbedsPage}
            />
          </div>
        )}

        {error ? (
          <p role="alert" aria-live="assertive" className="mt-4 text-sm font-medium text-error-500">
            {error}
          </p>
        ) : null}

        {!isLockedGroup && (
          <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
            <button type="button" onClick={goBack} className={`min-h-11 ${btnSecondary}`}>
              {isEdit ? "Volver" : "Cancelar"}
            </button>
            <button type="submit" disabled={saving} className={`min-h-11 ${btnSave}`}>
              {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear grupo"}
            </button>
          </div>
        )}
      </form>
      </EntityDetailNavLayout>

      <ConfirmDialog
        isOpen={seedbedToUnassign !== null}
        title={`¿Quitar el grupo de "${seedbedToUnassign?.name ?? ""}"?`}
        message="Se eliminarán las inscripciones de sus estudiantes en ese semillero."
        confirmLabel="Quitar"
        confirming={unassigningSeedbedId === seedbedToUnassign?.id}
        onConfirm={async () => {
          if (!seedbedToUnassign) return
          await onUnassignSeedbed(seedbedToUnassign.id)
          setSeedbedToUnassign(null)
        }}
        onCancel={() => setSeedbedToUnassign(null)}
      />
    </ModuleLayout>
  )
}
